import React from 'react';
import { isDefined } from '@dxb-web-ui-framework/common/utils';
import { AccountBalance } from '@dxb-web-ui-framework/design-system/dist/icons';
import { Box, makeStyles } from '@dxb-web-ui-framework/design-system';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { ConsolidatedTransactionDetails } from '../../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { OrderStatus } from '../../../../transaction/src/domain/Transaction/entities/OrderStatus';
import { ResidentBankLogo } from '../../../../transaction/src/components/TransactionDetails/ResidentBankLogo';
import { useWizardStepData } from '../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { Currency } from '../../domain/Currency/entities';
import { RoutingTransferTypes } from '../../domain/Transfer/entities/RoutingResponseDTO';
import { transactionUILabelValuesEnabler } from '../../utils';
import { MakeTransferResponseBody } from '../../domain/Transfer/entities/MakeTransferResponseDTO';
import { SwiftCode } from '../../domain/Transfer/entities';

const useStyles = makeStyles(theme => ({
  defaultBackgroundColor: {
    backgroundColor: theme.palette.background.default,
  },
  beneficiaryBankIcon: {
    marginRight: theme.spacing(1),
  },
}));

const useFundsTransferUILogicDisplayer = (
  stepIdentifier: string,
  makeTransferData?: MakeTransferResponseBody,
  transactionDetailsData?: ConsolidatedTransactionDetails,
) => {
  const initTransferData = useWizardStepData(stepIdentifier) as { values: GenericObject };
  const {
    values: {
      forecastFormData,
      forecastResponseData,
      otherMandatoryDataForUI,
      routingResponseData,
      routingFormData,
      bankCountry,
      beneficiaryCountry,
      selectedToCurrency,
      forecastRoutingResponseData,
    },
  } = initTransferData;


  const language = useLanguage();
  const classes = useStyles();
  const transferType = routingResponseData.transferType;

  const {
    shouldShowCharges,
    shouldShowCommissions,
    shouldShowExpenses,
    shouldShowTaxes,
    shouldShowBankName,
    shouldShowBankBIC,
    shouldShowBeneficiaryCountry,
    shouldShowBeneficiaryAddress,
    shouldShowExchangeRates,
  } = transactionUILabelValuesEnabler(transferType);

  const isOneColumnGrid =
    transferType === RoutingTransferTypes.INTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.INTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY;

  const shouldConsiderForecastFormData =
    transferType === RoutingTransferTypes.EXTERNAL_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_SAME_CURRENCY ||
    transferType === RoutingTransferTypes.EXTERNAL_UNKNOWN_BANK_OTHER_CURRENCY;

  const debitCurrency = (otherMandatoryDataForUI.currencies as Currency[]).find(c =>
    transactionDetailsData?.accountCurrency
      ? c.shortDescription === transactionDetailsData.accountCurrency
      : c.serialNumber === routingResponseData.debitCurrency,
  ) as Currency;

  const creditCurrency = (otherMandatoryDataForUI.currencies as Currency[]).find(c =>
    transferType === RoutingTransferTypes.INTERNAL_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.INTRABANK_OTHER_CURRENCY ||
    transferType === RoutingTransferTypes.CORPORATE_INTERNAL_OTHER_CURRENCY
      ? typeof selectedToCurrency === 'string'
        ? c.serialNumber === Number(selectedToCurrency)
        : c.shortDescription === selectedToCurrency?.shortDescription ||
          (!selectedToCurrency && c.serialNumber === Number(routingFormData.orderCurrency))
      : transactionDetailsData?.exchangeCurrency
      ? c.shortDescription === transactionDetailsData.exchangeCurrency
      : c.serialNumber === Number(routingFormData.orderCurrency),
  ) as Currency;

  const exchangeCurrency = (otherMandatoryDataForUI.currencies as Currency[]).find(
    c => c.serialNumber === Number(routingFormData.orderCurrency),
  ) as Currency;

  const isDebitCurrencySameWithOrderCurrency =
    debitCurrency.serialNumber ===
    (routingFormData?.orderCurrency ? Number(routingFormData?.orderCurrency) : routingFormData?.orderCurrency);

  const formatAmountHelper = (value?: number) => {
    if (stepIdentifier === 'initTransfer') {
      return formatAmount(
        value as number,
        shouldShowExchangeRates
          ? isDebitCurrencySameWithOrderCurrency
            ? (debitCurrency.shortDescription as string)
            : (creditCurrency.shortDescription as string)
          : (debitCurrency.shortDescription as string),
        2,
        language.cultureName,
      );
    }

    if (!isDefined(value) || !transactionDetailsData) {
      return '-';
    }

    return formatAmount(value as number, transactionDetailsData.accountCurrency, 2, language.cultureName);
  };

  const renderBeneficiaryBankName = () => {
    if (transactionDetailsData?.bankName === 'ECOMMBX LIMITED' || routingResponseData.ecommbx) {
      return <ResidentBankLogo resident='EcommBX' />;
    }

    if (transactionDetailsData?.bankName) {
      return (
        <Box display='flex' justifyContent='center' p={1} className={classes.defaultBackgroundColor}>
          <AccountBalance className={classes.beneficiaryBankIcon} />
          {transactionDetailsData.bankName}
        </Box>
      );
    }

    return (
      <Box display='flex' justifyContent='center' p={1} className={classes.defaultBackgroundColor}>
        <AccountBalance className={classes.beneficiaryBankIcon} />
        {shouldConsiderForecastFormData
          ? forecastFormData.bankName
          : routingResponseData?.bankName && routingResponseData.bankName}
      </Box>
    );
  };

  const amount = () => {
    if (transactionDetailsData && transactionDetailsData?.exchangeCurrency && transactionDetailsData?.amount) {
      return formatAmount(
        transactionDetailsData.amount,
        transactionDetailsData.exchangeCurrency,
        2,
        language.cultureName,
      );
    }
    return '-';
  };

  const currency = () => {
    if (transactionDetailsData) {
      if (shouldShowExchangeRates) {
        return transactionDetailsData.exchangeCurrency;
      }
      return transactionDetailsData.accountCurrency;
    }
    return '-';
  };

  // helpers
  const isTransactionSettled = () => {
    if (isDefined(makeTransferData)) {
      return (
        ((otherMandatoryDataForUI.orderStatuses as OrderStatus[]).find(os => os.serialNumber === 3) as OrderStatus)
          .shortDescription === makeTransferData?.processingResult
      );
    }

    return false;
  };

  return {
    forecastFormData,
    forecastResponseData,
    otherMandatoryDataForUI,
    routingResponseData,
    routingFormData,
    shouldShowCharges,
    shouldShowCommissions,
    shouldShowExpenses,
    shouldShowTaxes,
    shouldShowBankName,
    shouldShowBankBIC,
    shouldShowBeneficiaryCountry,
    shouldShowBeneficiaryAddress,
    shouldShowExchangeRates,
    isOneColumnGrid,
    debitCurrency,
    creditCurrency,
    shouldConsiderForecastFormData,
    formatAmountHelper,
    renderBeneficiaryBankName,
    amount: amount(),
    currency: currency(),
    exchangeCurrency,
    isTransactionSettled,
    transferType,
    bankCountry,
    beneficiaryCountry,
    selectedBankSwiftCode: initTransferData.values?.selectedBankSwiftCode as Nullable<SwiftCode>,
    isExternalTransferType: shouldConsiderForecastFormData,
    isDebitCurrencySameWithOrderCurrency,
    forecastRoutingResponseData,
  };
};

export { useFundsTransferUILogicDisplayer };
